import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { BrowserModule } from '@angular/platform-browser';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { LocationStrategy, HashLocationStrategy } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatInputModule } from '@angular/material/input';
import { MatSortModule } from '@angular/material/sort';
import { AngularMaterial } from './angular-material';
import { MomentModule } from 'ngx-moment';
import { InputTextareaModule } from 'primeng/inputtextarea';

import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { PERFECT_SCROLLBAR_CONFIG } from 'ngx-perfect-scrollbar';
import { PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true
};

import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastrModule } from 'ngx-toastr';
import { TreeModule } from 'primeng/tree';
import { TreeViewModule } from '@progress/kendo-angular-treeview';
import { LeafletDrawModule } from '@asymmetrik/ngx-leaflet-draw';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';

// Import containers
import { DefaultLayoutComponent } from './containers';

import { P404Component } from './views/error/404.component';
import { P500Component } from './views/error/500.component';
import { LoginComponent } from './views/login/login.component';
import { RegisterComponent } from './views/register/register.component';

const APP_CONTAINERS = [
  DefaultLayoutComponent
];

import {
  AppAsideModule,
  AppBreadcrumbModule,
  AppHeaderModule,
  AppFooterModule,
  AppSidebarModule,
} from '@coreui/angular';

// Import routing module
import { AppRoutingModule } from './app.routing';

// Import 3rd party components
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { ChartsModule } from 'ng2-charts';
import { ngModuleJitUrl } from '@angular/compiler';
import { ConfirmationDialogComponent } from './views/shared/confirmation-dialog/confirmation-dialog.component';
import { AttachFilesDialogComponent } from './views/shared/attach-files-dialog/attach-files-dialog.component';
import { CommentsComponent } from './views/shared/comments/comments.component';
import { LoaderComponent } from './views/shared/loader/loader.component';
import { CheckboxModule } from 'primeng/checkbox';

// Import Translate module
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LoaderService } from './services/loader.service';
import { LoaderInterceptor } from './services/loader.interceptor';
import { ErrorInterceptor } from './services/error.interceptor';
import { TableEditComponent } from './views/shared/table/table-edit/table-edit.component';
import { DropdownGetDisplayPipe } from './views/shared/custom-pipe/dropdown-get-display.pipe';
import { GetEnumValuePipe } from './views/shared/custom-pipe/get-enum-value.pipe';
import { SharedAllModule } from './views/shared/shared-all.module';
import { PictureGalleryComponent } from './views/shared/picture-gallery/picture-gallery.component';
import { GalleriaModule } from 'primeng/galleria';
import { AttachFilesComponent } from './views/shared/attach-files/attach-files-dialog.component';

export function translateHttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  imports: [
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateHttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    AppAsideModule,
    AppBreadcrumbModule.forRoot(),
    AppFooterModule,
    AppHeaderModule,
    AppSidebarModule,
    PerfectScrollbarModule,
    BsDropdownModule.forRoot(),
    TabsModule.forRoot(),
    ChartsModule,
    HttpClientModule,
    ToastrModule.forRoot(),
    TreeModule,
    MatTableModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatInputModule,
    MatSortModule,
    AngularMaterial,
    MomentModule,
    InputTextareaModule,
    TreeViewModule,
    InputsModule,
    LeafletModule,
    LeafletDrawModule,
    TableModule,
    TooltipModule,
    CheckboxModule,
    SharedAllModule,
    GalleriaModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  declarations: [
    AppComponent,
    // ...APP_CONTAINERS,
    DefaultLayoutComponent,
    P404Component,
    P500Component,
    LoginComponent,
    RegisterComponent,
    ConfirmationDialogComponent,
    AttachFilesDialogComponent,
    CommentsComponent,
    LoaderComponent,
    TableEditComponent,
    DropdownGetDisplayPipe,
    GetEnumValuePipe,
    PictureGalleryComponent
  ],
  entryComponents: [
    ConfirmationDialogComponent,
    AttachFilesDialogComponent,
    AttachFilesComponent,
    CommentsComponent,
    TableEditComponent,
    PictureGalleryComponent
  ],
  providers: [{
    provide: LocationStrategy,
    useClass: HashLocationStrategy
  },
    HttpClientModule,
    LoaderService,
  {
    provide: HTTP_INTERCEPTORS,
    useClass: LoaderInterceptor,
    multi: true
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: ErrorInterceptor,
    multi: true
  }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
